"use client";

import { type ReactNode, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  DEFAULT_OPTIONS,
  type FieldKey,
  type FormatError,
  type FormatOptions,
  formatStatistic,
  type Segment,
  STAT_FIELDS,
  type StatType,
  toHtml,
  toPlainText,
} from "@/lib/tool-kit/stats-distributions-more/apa-stats";

/** English strings. A translated content file overrides these through `ui`. */
export const APA_STATISTICS_FORMATTER_STRINGS = {
  typeLabel: "What are you reporting?",
  typeT: "t test",
  typeF: "F test (ANOVA)",
  typeChi: "Chi-square test",
  typeR: "Correlation (r)",
  typeZ: "z test",
  typeMean: "Mean and standard deviation",
  typeModel: "Regression model (R², F)",
  typeCoef: "Regression coefficient (b, β)",
  typeOr: "Odds ratio",
  fieldT: "t value",
  fieldDf: "Degrees of freedom (df)",
  fieldDf1: "Degrees of freedom, numerator (df₁)",
  fieldDf2: "Degrees of freedom, denominator (df₂)",
  fieldChi: "χ² value",
  fieldN: "Sample size (N), optional",
  fieldF: "F value",
  fieldR: "Correlation (r)",
  fieldZ: "z value",
  fieldM: "Mean (M)",
  fieldSD: "Standard deviation (SD)",
  fieldR2: "R² (proportion of variance explained)",
  fieldB: "Unstandardized coefficient (b)",
  fieldBeta: "Standardized coefficient (β), optional",
  fieldOR: "Odds ratio (OR)",
  fieldP: "p value, as exact as you have it",
  fieldD: "Cohen's d, optional",
  fieldV: "Cramér's V, optional",
  fieldEta: "Partial eta squared (ηp²), optional",
  fieldCiLevel: "Confidence level (%), default 95",
  fieldCiLow: "Confidence interval, lower limit (optional)",
  fieldCiHigh: "Confidence interval, upper limit (optional)",
  pDecimalsLabel: "Exact p to",
  pThree: "Three decimals",
  pTwo: "Two decimals (three below .01)",
  meanDecimalsLabel: "M and SD to",
  meanOne: "One decimal (whole-number scales)",
  meanTwo: "Two decimals",
  plainMinus: "Use a plain hyphen for negative numbers (−3.1 becomes -3.1)",
  results: "Your APA 7 result",
  copyFormatted: "Copy with formatting",
  copyPlain: "Copy plain text",
  copied: "Copied!",
  formattingNote:
    "Symbols are italic where APA 7 requires it; paste with formatting into Word or Google Docs to keep the italics. Numbers use a decimal point, as English-language APA papers do.",
  rulesHeading: "Rules applied",
  ruleItalics:
    "Italic for letters used as statistical symbols (t, F, p, r, M, SD, N, d, R); upright for Greek letters (χ², η², β) and abbreviations such as OR and CI.",
  ruleZero:
    "No zero before the decimal point for statistics that cannot exceed 1 (p, r, R², η², V); a zero for those that can (t, F, χ², z, d, M, SD, b, OR).",
  rulePlaces:
    "Two decimals for inferential statistics, correlations and effect sizes; exact p to two or three decimals, and p < .001 below that.",
  ruleSpacing:
    "A space before and after = and <; no comma in degrees of freedom; confidence intervals as 95% CI [lower, upper].",
  source:
    "Source: APA Style, Numbers and Statistics Guide (7th ed.), citing Publication Manual sections 6.36 and 6.40–6.45.",
  errMissing: "Enter a value for “{field}”.",
  errInvalid: "“{field}” is not a number.",
  errRange: "“{field}” is outside the range it can take.",
  errInterval:
    "Enter both ends of the confidence interval, with the lower limit first.",
} as const;

type Strings = typeof APA_STATISTICS_FORMATTER_STRINGS;
type Values = Partial<Record<FieldKey, string>>;

const FIELD_LABELS: Record<FieldKey, keyof Strings> = {
  t: "fieldT",
  df: "fieldDf",
  df1: "fieldDf1",
  df2: "fieldDf2",
  chi: "fieldChi",
  N: "fieldN",
  F: "fieldF",
  r: "fieldR",
  z: "fieldZ",
  M: "fieldM",
  SD: "fieldSD",
  R2: "fieldR2",
  b: "fieldB",
  beta: "fieldBeta",
  OR: "fieldOR",
  p: "fieldP",
  d: "fieldD",
  V: "fieldV",
  eta: "fieldEta",
  ciLevel: "fieldCiLevel",
  ciLow: "fieldCiLow",
  ciHigh: "fieldCiHigh",
};

/** An example for each type, so there is always a result to read and copy. */
const EXAMPLES: Record<StatType, Values> = {
  t: {
    t: "2.4531",
    df: "28",
    p: "0.02059",
    d: "0.8957",
    ciLow: "0.1215",
    ciHigh: "1.6704",
    ciLevel: "95",
  },
  f: { F: "4.213", df1: "2", df2: "57", p: "0.0197", eta: "0.1288" },
  chi: { chi: "6.351", df: "2", N: "120", p: "0.0418", V: "0.2301" },
  r: {
    r: "0.4812",
    df: "48",
    p: "0.00037",
    ciLow: "0.2312",
    ciHigh: "0.6711",
    ciLevel: "95",
  },
  z: { z: "1.96", p: "0.05" },
  mean: { M: "7.7", SD: "1.3" },
  model: { R2: "0.12", F: "7.331", df1: "1", df2: "32", p: "0.0107" },
  coef: {
    b: "0.4521",
    beta: "0.3012",
    t: "2.5",
    df: "48",
    p: "0.0158",
    ciLow: "0.0911",
    ciHigh: "0.8131",
    ciLevel: "95",
  },
  or: { OR: "2.02", p: "0.004", ciLow: "1.7", ciHigh: "2.5", ciLevel: "95" },
};

function errorText(error: FormatError, strings: Strings): string {
  if (error.kind === "interval") {
    return strings.errInterval;
  }
  const field = strings[FIELD_LABELS[error.field]];
  const templates = {
    missing: strings.errMissing,
    invalid: strings.errInvalid,
    range: strings.errRange,
  };
  return t(templates[error.kind], { field });
}

function Rendered({ segments }: { segments: readonly Segment[] }) {
  return (
    <>
      {segments.map((segment, index) => {
        let node: ReactNode = segment.text;
        if (segment.italic) {
          node = <i>{node}</i>;
        }
        if (segment.sup) {
          node = <sup>{node}</sup>;
        }
        if (segment.sub) {
          node = <sub>{node}</sub>;
        }
        // Segments of one result: position is their identity.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional segments
        return <span key={index}>{node}</span>;
      })}
    </>
  );
}

export default function ApaStatisticsFormatterWidget() {
  const strings = useToolUi(APA_STATISTICS_FORMATTER_STRINGS);
  const [type, setType] = useState<StatType>("t");
  const [byType, setByType] = useState<Record<StatType, Values>>(EXAMPLES);
  const [options, setOptions] = useState<FormatOptions>(DEFAULT_OPTIONS);
  const values = byType[type];
  const setValue = (key: FieldKey, value: string) =>
    setByType((all) => ({ ...all, [type]: { ...all[type], [key]: value } }));
  const spec = STAT_FIELDS[type];
  const result = formatStatistic(type, values, options, parseNumberInput);
  const typeOptions: { value: StatType; label: string }[] = [
    { value: "t", label: strings.typeT },
    { value: "f", label: strings.typeF },
    { value: "chi", label: strings.typeChi },
    { value: "r", label: strings.typeR },
    { value: "z", label: strings.typeZ },
    { value: "mean", label: strings.typeMean },
    { value: "model", label: strings.typeModel },
    { value: "coef", label: strings.typeCoef },
    { value: "or", label: strings.typeOr },
  ];

  return (
    <>
      <section className="space-y-5">
        <SelectField
          id="apa-type"
          label={strings.typeLabel}
          onChange={setType}
          options={typeOptions}
          value={type}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          {[...spec.required, ...spec.optional].map((key) => (
            <TextField
              id={`apa-${key}`}
              key={`${type}-${key}`}
              label={strings[FIELD_LABELS[key]]}
              numeric
              onChange={(value) => setValue(key, value)}
              value={values[key] ?? ""}
            />
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <PillGroup
            label={strings.pDecimalsLabel}
            name="apa-p"
            onChange={(value) =>
              setOptions((old) => ({
                ...old,
                pDecimals: value === "2" ? 2 : 3,
              }))
            }
            options={[
              { value: "3", label: strings.pThree },
              { value: "2", label: strings.pTwo },
            ]}
            value={String(options.pDecimals)}
          />
          {type === "mean" ? (
            <PillGroup
              label={strings.meanDecimalsLabel}
              name="apa-mean"
              onChange={(value) =>
                setOptions((old) => ({
                  ...old,
                  meanDecimals: value === "1" ? 1 : 2,
                }))
              }
              options={[
                { value: "2", label: strings.meanTwo },
                { value: "1", label: strings.meanOne },
              ]}
              value={String(options.meanDecimals)}
            />
          ) : null}
        </div>
        <Checkbox
          checked={options.plainMinus}
          label={strings.plainMinus}
          onChange={(plainMinus) =>
            setOptions((old) => ({ ...old, plainMinus }))
          }
        />
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {result.ok ? (
          <>
            <div className={`${CARD} space-y-3`}>
              <p className="break-words text-foreground text-xl">
                <Rendered segments={result.segments} />
              </p>
              <div className="flex flex-wrap gap-3">
                <CopyButton
                  copiedLabel={strings.copied}
                  html={toHtml(result.segments)}
                  label={strings.copyFormatted}
                  text={toPlainText(result.segments)}
                />
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copyPlain}
                  text={toPlainText(result.segments)}
                />
              </div>
              <p className="text-muted-foreground text-xs">
                {strings.formattingNote}
              </p>
            </div>
            <div className={`${CARD} space-y-2`}>
              <h3 className={WIDGET_H3}>{strings.rulesHeading}</h3>
              <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm">
                <li>{strings.ruleItalics}</li>
                <li>{strings.ruleZero}</li>
                <li>{strings.rulePlaces}</li>
                <li>{strings.ruleSpacing}</li>
              </ul>
              <p className="text-muted-foreground text-xs">{strings.source}</p>
            </div>
          </>
        ) : (
          <Notice>{errorText(result.error, strings)}</Notice>
        )}
      </section>
    </>
  );
}
